import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { adminApi, type CourseAnalyticsDto } from '@/api/admin'
import { EmptyChart, SectionTitle, SortableTh } from './AnalyticsShared'
import { fmtRating, useSortable, useAnalyticsMoney } from './analyticsHelpers'

export function CoursesTab({ from, to }: { from: string | null; to: string | null }) {
  const { fmtMoney, fmtMoneyShort } = useAnalyticsMoney()
  const { data = [], isLoading } = useQuery({
    queryKey: ['analytics-courses', from, to],
    queryFn: () => adminApi.getCourseAnalytics(from, to).then(r => r.data),
  })

  const { sorted, toggle, sortKey, sortDir } = useSortable<CourseAnalyticsDto>(data, 'revenue')
  const hasLive = data.some(c => c.live)

  const chartData = [...data]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10)
    .map(c => ({
      name: c.courseName.length > 18 ? c.courseName.slice(0, 18) + '…' : c.courseName,
      revenue: c.revenue,
      bookings: c.completedBookings,
    }))

  if (isLoading) return <p className="text-gray-400 text-sm py-8">Loading…</p>

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Revenue by course (top 10)</SectionTitle>
          <div className="mt-4 h-56">
            {chartData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [fmtMoney(Number(v)), 'Revenue']} />
                  <Bar dataKey="revenue" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <SectionTitle>Completed sessions by course</SectionTitle>
          <div className="mt-4 h-56">
            {chartData.length === 0 ? <EmptyChart /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [Number(v), 'Sessions']} />
                  <Bar dataKey="bookings" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <SortableTh col="courseName"        sortKey={sortKey} sortDir={sortDir} onToggle={toggle}>Course</SortableTh>
              <SortableTh col="totalBookings"     sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Bookings</SortableTh>
              <SortableTh col="completedBookings" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Completed</SortableTh>
              <SortableTh col="cancelledBookings" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Cancelled</SortableTh>
              <SortableTh col="revenue"           sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Revenue</SortableTh>
              <SortableTh col="averageRating"     sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Avg rating</SortableTh>
              <SortableTh col="feedbackCount"     sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Reviews</SortableTh>
              <SortableTh col="certificatesIssued" sortKey={sortKey} sortDir={sortDir} onToggle={toggle} right>Certs</SortableTh>
              {hasLive && <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" title="Ended CertSlate Live sessions">Live sessions</th>}
              {hasLive && <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" title="Weighted by responses">Attendee feedback</th>}
              {hasLive && <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" title="Post-course vs pre-course, percentage points">Learning gain</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr><td colSpan={hasLive ? 11 : 8} className="px-4 py-10 text-center text-gray-400 text-sm">No course data for this period</td></tr>
            ) : sorted.map(c => (
              <tr key={c.courseId} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{c.courseName}</p>
                  {c.courseCode && <p className="text-xs text-gray-400">{c.courseCode}</p>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.totalBookings}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-700 font-medium">{c.completedBookings}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-500">{c.cancelledBookings}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">{fmtMoney(c.revenue)}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={c.averageRating != null ? (c.averageRating >= 8 ? 'text-green-600 font-medium' : c.averageRating >= 6 ? 'text-amber-600' : 'text-red-600') : 'text-gray-300'}>
                    {fmtRating(c.averageRating)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-500">{c.feedbackCount}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.certificatesIssued}</td>
                {hasLive && <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.live?.sessions ?? <span className="text-gray-300">—</span>}</td>}
                {hasLive && <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.live?.feedbackAverage != null ? `${c.live.feedbackAverage.toFixed(1)}/10` : <span className="text-gray-300">—</span>}</td>}
                {hasLive && <td className="px-4 py-3 text-right tabular-nums text-gray-600">{c.live?.learningGain != null ? `${c.live.learningGain >= 0 ? '+' : ''}${c.live.learningGain.toFixed(1)} pts` : <span className="text-gray-300">—</span>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
